import { create } from "zustand";
import type { AddressFields } from "@/types/Address";
import type { BankDetails, SubmissionParams } from "@/types/Payment";
import { setValueAtPath } from "@/utils/setValueAtPath";

export enum DonationType {
  OneOff = "one-off",
  Monthly = "monthly",
}

export type PersonalDetails = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
};

export type CardImage = {
  src: string;
  alt: string;
};

export type CardType = "" | "none" | "post" | "e-card";

export type CardSelector = {
  cardType: CardType;
  cardImage: CardImage;
  celebrationType: string;
  honouringForename: string;
  honouringSurname: string;
  sendCardToForename: string;
  sendCardToSurname: string;
  postCardAddress: AddressFields;
  recipient: {
    email: string;
    confirmEmail: string;
  };
  message: string;
};

export type DonationFormStateValues = {
  nonce: string;
  donationType: `${DonationType}` | null;
  donationAmount: number | null;
  donationOneOffType: string | null;
  isUKTaxPayer: boolean | null;
  personalDetails: PersonalDetails;
  bankDetails: BankDetails;
  donationSource: string;
  onBehalf: string;
  organisationName: string;
  cardEnabled: boolean;
  paymentMethod: string | null;
  hasCustomAmount: boolean;
  signUpFrontline: boolean;
  address: AddressFields;
  isManualAddress: boolean;
  cardSelector: CardSelector;
};

export type DonationFormState = DonationFormStateValues & {
  setFieldValue: (path: string, value: unknown) => void;
  setDonationType: (tabName: `${DonationType}`) => void;
  setDonationAmount: (amount: null | number) => void;
  setDonationOneOffType: (type: string) => void;
  setUKTaxPayer: (isUKTaxPayer: boolean | null) => void;
  reset: () => void;
  setPersonalDetails: (personalDetails: PersonalDetails) => void;
  setBankDetails: (bankPayment: Partial<BankDetails>) => void;
  setDonationSource: (source: string) => void;
  setOnBehalf: (onBehalf: string) => void;
  setOrganisationName: (organisationName: string) => void;
  setCardEnabled: (enabled: boolean) => void;
  setPaymentMethod: (method: string) => void;
  setHasCustomAmount: (hasCustomAmount: boolean) => void;
  setAddress: (address: AddressFields) => void;
  setSignUpFrontline: (signUpFrontline: boolean) => void;
  getSubmissionFields: () => SubmissionParams["fields"];
  setNonce: (nonce: string) => void;
  setIsManualAddress: (isManualAddress: boolean) => void;
  getPayment: () => {
    nonce: string;
  } | null;
};

const initialState: DonationFormStateValues = {
  nonce: "",
  isManualAddress: false,
  donationType: null,
  donationAmount: null,
  donationOneOffType: null,
  isUKTaxPayer: null,
  cardEnabled: false,
  paymentMethod: null,
  personalDetails: {
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
  },
  bankDetails: {
    account_holder: "",
    account_number: "",
    sort_code: "",
    start_date: "",
  },
  donationSource: "",
  onBehalf: "",
  organisationName: "",
  hasCustomAmount: false,
  signUpFrontline: false,
  address: {
    "address-line-1": "",
    "address-line-2": "",
    city: "",
    country: "",
    "postal-code": "",
    region: "",
  },
  cardSelector: {
    cardType: "",
    celebrationType: "",
    cardImage: {
      src: "",
      alt: "",
    },
    honouringForename: "",
    honouringSurname: "",
    sendCardToForename: "",
    sendCardToSurname: "",
    postCardAddress: {
      "address-line-1": "",
      "address-line-2": "",
      city: "",
      region: "",
      "postal-code": "",
      country: "",
    },
    recipient: {
      email: "",
      confirmEmail: "",
    },
    message: "",
  },
};

export const useDonationFormStore = create<DonationFormState>((set, get) => ({
  ...initialState,
  setFieldValue: (path, value) => {
    const currentState = get();
    const updatedState = setValueAtPath(currentState, path, value);
    set(updatedState);
  },
  setNonce: (nonce) => set({ nonce }),
  setDonationType: (type) =>
    set({ donationType: type, cardEnabled: false, donationOneOffType: null }),
  setDonationAmount: (amount) => set({ donationAmount: amount }),
  setDonationOneOffType: (type) => set({ donationOneOffType: type }),
  setUKTaxPayer: (isUKTaxPayer) => set({ isUKTaxPayer }),
  reset: () => set(initialState),
  setPersonalDetails: (personalDetails) => set({ personalDetails }),
  setBankDetails: (bankDetails) => {
    const currentBankDetails = get().bankDetails;
    set({
      bankDetails: {
        ...currentBankDetails,
        ...bankDetails,
      },
    });
  },
  setDonationSource: (source) => set({ donationSource: source }),
  setOnBehalf: (onBehalf) => set({ onBehalf }),
  setOrganisationName: (organisationName) => set({ organisationName }),
  setCardEnabled: (enabled) => set({ cardEnabled: enabled }),
  setPaymentMethod: (method) => set({ paymentMethod: method }),
  setHasCustomAmount: (hasCustomAmount) => set({ hasCustomAmount }),
  setAddress: (address) => set({ address }),
  setSignUpFrontline: (signUpFrontline) => set({ signUpFrontline }),
  setIsManualAddress: (isManualAddress) => set({ isManualAddress }),
  getPayment: () => {
    const nonce = get().nonce;

    if (!nonce) {
      return null;
    }

    return {
      nonce,
    };
  },
  getSubmissionFields: (): SubmissionParams["fields"] => {
    const {
      donationOneOffType,
      isUKTaxPayer,
      cardEnabled,
      bankDetails,
      donationType,
      donationSource,
    } = get();

    const cardSelectorState = get().cardSelector;
    const cardType = cardSelectorState.cardType;

    const cardSelector = {
      card_type: cardType,
      celebration_type: cardSelectorState.celebrationType,
      ...(cardType !== "none" && {
        selected_card: cardSelectorState.cardImage?.src ?? "",
        selected_card_title: cardSelectorState.cardImage?.alt ?? "",
      }),
      honoree: {
        forename: cardSelectorState.honouringForename,
        surname: cardSelectorState.honouringSurname,
      },
      ...(cardType !== "none" && {
        recipient: {
          forename: cardSelectorState.sendCardToForename,
          surname: cardSelectorState.sendCardToSurname,
          email: cardSelectorState.recipient.email,
          confirm_email: cardSelectorState.recipient.confirmEmail,
          address_line_1: cardSelectorState.postCardAddress["address-line-1"],
          address_line_2: cardSelectorState.postCardAddress["address-line-2"],
          city: cardSelectorState.postCardAddress.city,
          postal_code: cardSelectorState.postCardAddress["postal-code"],
          country: cardSelectorState.postCardAddress.country,
        },
      }),
      message: cardSelectorState.message,
    };

    const submission = {
      amount: {
        amount: get().donationAmount!,
        donation_type: donationType!,
      },
      ...(donationOneOffType && {
        donation_intent: {
          donation_intent: donationOneOffType,
        },
      }),
      gift_aid: {
        gift_aid: donationSource === "own-money" && !!isUKTaxPayer,
      },
      personal_details: {
        title: get().personalDetails.title,
        first_name: get().personalDetails.firstName,
        last_name: get().personalDetails.lastName,
        email: get().personalDetails.email,
      },
      your_address: {
        address_line_1: get().address["address-line-1"],
        address_line_2: get().address["address-line-2"],
        city: get().address.city,
        region: get().address.region,
        postal_code: get().address["postal-code"],
        country: get().address.country,
      },
      ...(cardEnabled && { card_selector: cardSelector }),
      sign_up_to_frontline: {
        sign_up_frontline: get().signUpFrontline,
      },
      donation_source: {
        donation_source: donationSource,
        donation_behalf: get().onBehalf,
        donation_org_name: get().organisationName,
      },
      ...(donationType === "monthly" && {
        bank_details: {
          account_holder: bankDetails.account_holder,
          account_number: bankDetails.account_number,
          sort_code: bankDetails.sort_code,
          start_date: bankDetails.start_date,
        },
      }),
      ...(donationType === "one-off" && {
        payment_methods: {
          payment_method: get().paymentMethod!,
        },
      }),
    };

    return submission;
  },
}));

export function getCurrentStateValues(): DonationFormStateValues {
  const state = useDonationFormStore.getState();
  return {
    nonce: state.nonce,
    donationType: state.donationType,
    donationAmount: state.donationAmount,
    donationOneOffType: state.donationOneOffType,
    isUKTaxPayer: state.isUKTaxPayer,
    personalDetails: state.personalDetails,
    bankDetails: state.bankDetails,
    donationSource: state.donationSource,
    onBehalf: state.onBehalf,
    organisationName: state.organisationName,
    cardEnabled: state.cardEnabled,
    paymentMethod: state.paymentMethod,
    hasCustomAmount: state.hasCustomAmount,
    signUpFrontline: state.signUpFrontline,
    address: state.address,
    isManualAddress: state.isManualAddress,
    cardSelector: state.cardSelector,
  };
}
